/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { Link } from 'react-router';

import gql from 'graphql-tag';
import { compact, sortBy } from 'lodash';
import { compose } from 'recompose';

import logger from 'js/app/loggerSingleton';
import waitForGraphQL from 'js/lib/waitForGraphQL';

import { Typography2, typography2, useTheme } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';
import type { Country } from '@coursera/grpc-types-countrynames/coursera/proto/countrynames/v1/country_names';
import type { MultiGetCountryNamesResponse } from '@coursera/grpc-types-countrynames/coursera/proto/countrynames/v1/country_names_api';

import { isPartnersPageMigrationEnabled } from 'bundles/partners-graphql/utils/experiments';

import _t from 'i18n!nls/partners-graphql';

const styles = {
  countryTags: (theme: Theme) => css`
    margin: 30px 20px;
    align-items: center;
    justify-content: center;
    line-height: 1.6em;

    .index-item {
      ${typography2.bodySecondary}
      .index-item-delimiter {
        color: rgb(0 0 0 / 26%);
        margin: 0 14px;

        &:last-child {
          .index-item-delimiter {
            display: none;
          }
        }
      }
    }
  `,
};

type PropsFromCaller = {
  selectedCountry: string;
  countryCodes: string[];
};

type PropsFromGraphQL = {
  countries: Array<Country>;
};

type Props = PropsFromCaller & PropsFromGraphQL;

export const CountryTags = ({ countries, selectedCountry = '' }: Props) => {
  const theme = useTheme();

  if (!countries || countries.length < 1) {
    return null;
  }

  const sortedCountries = () => {
    return compact(sortBy(countries, 'name'));
  };

  const CountryLink = ({ countryCode, label }: { countryCode: string; label: string }) => {
    const isNewPartnerPages = isPartnersPageMigrationEnabled();
    const countryLink =
      countryCode !== '' ? (
        <Link to={`${isNewPartnerPages ? '' : '/about'}/partners?countryCode=${countryCode.toLowerCase()}`}>
          {label}
        </Link>
      ) : (
        <Link to={`${isNewPartnerPages ? '' : '/about'}/partners`}>{label}</Link>
      );

    return (
      <span className="index-item" key={countryCode}>
        {selectedCountry.toLowerCase() === countryCode.toLowerCase() ? (
          <Typography2 component="span" variant="bodySecondary">
            {label}
          </Typography2>
        ) : (
          countryLink
        )}
        <Typography2 component="span" variant="bodySecondary" className="index-item-delimiter">
          |
        </Typography2>
      </span>
    );
  };

  return (
    <div css={styles.countryTags(theme)} className="horizontal-box wrap" data-e2e="country-tags">
      <CountryLink countryCode="" label={_t('All Countries and Regions')} />

      {sortedCountries().map((country) => {
        return <CountryLink key={country.isoAlpha2Code} countryCode={country.isoAlpha2Code} label={country.name} />;
      })}
    </div>
  );
};

export default compose<Props, PropsFromCaller>(
  waitForGraphQL(
    gql`
      query CountryNamesQuery($countryNamesQueryInput: MultiGetCountryNamesRequest!) {
        MultiGetCountryNames(input: $countryNamesQueryInput)
          @rest(
            type: "MultiGetCountryNames"
            path: "grpc/countrynames/v1/CountryNamesAPI/MultiGetCountryNames"
            method: "POST"
          ) {
          countries
        }
      }
    `,
    {
      options: ({ countryCodes }: Props) => {
        return {
          variables: {
            countryNamesQueryInput: {
              iso_alpha_2_code: countryCodes,
            },
          },
        };
      },
      props: ({ data }: { data?: { MultiGetCountryNames?: MultiGetCountryNamesResponse } }) => {
        const countries = data?.MultiGetCountryNames?.countries;

        if (!countries) {
          return {
            countries: null,
          };
        }

        const validCountries = countries.filter((country) => !!country);

        if (validCountries.length !== countries.length) {
          const count = countries.length - validCountries.length;
          logger.error(`Received null countries. Count${count}`);
        }

        return {
          countries: validCountries,
        };
      },
    }
  )
)(CountryTags);
